import {getUtcOffset} from '@hconnect/common/utils'
import {formatLocalizedDateMonth, shortDayNameFormatter} from '@hconnect/uikit/src/common'
import {TimeScaleFn} from '@widgets/common-d3-chart'
import moment from 'moment-timezone'
import {useMemo} from 'react'

interface FormatStepParams {
  xScale: TimeScaleFn
  timezone: string
  convertStepToWidth: (step: number) => number
  language: string
}
interface ShortDayParams {
  withShortDay: boolean
}

export const getFormatStepGenerator =
  ({xScale, timezone, convertStepToWidth, language}: FormatStepParams) =>
  ({withShortDay}: ShortDayParams) =>
  (step: number) => {
    const currentDate = moment.utc(xScale.invert(convertStepToWidth(step)))
    const dateInTz = currentDate.clone().tz(timezone)
    const dayAndMonth = formatLocalizedDateMonth(dateInTz, language)

    if (!withShortDay) return dayAndMonth
    const offset = getUtcOffset(timezone, currentDate)
    return `${shortDayNameFormatter(currentDate, language, offset)} ${dayAndMonth}`
  }

export const useFormatStep = ({
  xScale,
  timezone,
  convertStepToWidth,
  language
}: FormatStepParams) => {
  const formatStepGenerator = useMemo(
    () => getFormatStepGenerator({xScale, timezone, convertStepToWidth, language}),
    [xScale, timezone, convertStepToWidth, language]
  )

  const formatStepWithDay = useMemo(
    () => formatStepGenerator({withShortDay: true}),
    [formatStepGenerator]
  )
  const formatStepWithoutDay = useMemo(
    () => formatStepGenerator({withShortDay: false}),
    [formatStepGenerator]
  )
  return {formatStepWithDay, formatStepWithoutDay}
}
